import {
  Alert,
  Autocomplete,
  Button,
  FormControl,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  ChangeEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useGetMedicationsPerProductQuery } from "src/api/services/medicationsApi";
import { useUpdatePrescriptionMedicationsMutation } from "src/api/services/prescriptionsApi";
import { Medication } from "src/api/types";
import LoadingButton from "src/components/Buttons/LoadingButton";
import { useSentryLogger } from "src/hooks/useSentryLogger";

interface Props {
  item: {
    id: number;
    productId: number;
    patientId: number;
    count?: number;
    medication_id?: number;
  };
  onReturn: () => void;
}

const EditPrescriptionMedication = ({ item, onReturn }: Props) => {
  const { data: medications = [] } = useGetMedicationsPerProductQuery({
    product_id: item.productId,
    active: true,
  });
  const [updateMedication] = useUpdatePrescriptionMedicationsMutation();
  const [selectedMedication, setSelectedMedication] =
    useState<Medication | null>(null);
  const [vialQuantity, setVialQuantity] = useState<number>(item.count ?? 1);
  const [loading, setLoading] = useState<boolean>(false);
  const errorLog = useSentryLogger("error");

  const handleMedication = useCallback(
    (_: SyntheticEvent<Element | Event>, value: Medication | null) => {
      setSelectedMedication(value);
    },
    [],
  );

  const handleVialQuantity = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setVialQuantity(Number(e.target.value));
  }, []);

  const handleUpdate = useCallback(() => {
    if (selectedMedication == null) return;

    setLoading(true);
    try {
      updateMedication({
        medication_id: selectedMedication.id,
        prescription_id: item.id,
        vial_quantity: vialQuantity,
        pharmacy_id: selectedMedication.pharmacy_id,
      }).unwrap();

      setLoading(false);
    } catch (error) {
      errorLog(
        `Unable to update the prescription's medication. Prescription ID: ${item.id}`,
        error,
      );
    } finally {
      onReturn();
    }
  }, [
    errorLog,
    item.id,
    onReturn,
    selectedMedication,
    updateMedication,
    vialQuantity,
  ]);

  useEffect(() => {
    if (item.medication_id != null && medications.length > 0) {
      setSelectedMedication(
        medications.find((m) => m.id === item.medication_id) ?? null,
      );
    }
  }, [item.medication_id, medications]);

  return (
    <Stack gap={2} width={"50%"} alignSelf={"center"}>
      <Alert severity="warning">
        <Typography variant="h5">
          Edit prescription's medication for patient: {item.patientId}
        </Typography>
      </Alert>
      <Stack direction={"row"} spacing={2}>
        <FormControl fullWidth>
          <Autocomplete
            options={medications}
            getOptionLabel={(medication) =>
              `${medication.name} ${medication.dosage} [${medication.strength}] - ${medication.pharmacy.name}`
            }
            value={selectedMedication}
            onChange={handleMedication}
            renderInput={(params) => (
              <TextField {...params} label="Medication" required />
            )}
          />
        </FormControl>
        <FormControl fullWidth>
          <TextField
            required
            label="Vial Quantity"
            value={vialQuantity}
            onChange={handleVialQuantity}
            type="number"
          />
        </FormControl>
      </Stack>

      <LoadingButton
        loading={loading}
        onClick={handleUpdate}
        disabled={selectedMedication == null}
      >
        Update
      </LoadingButton>
      <Button variant="secondary" onClick={onReturn}>
        Return
      </Button>
    </Stack>
  );
};

export default EditPrescriptionMedication;
